// The gun shots and the hits of the map (R1007, R1009 to R1014,
// `ui-map-interface.md` §6.3.3).
//
// A gun fight raises one HIT event per victim per shot, once per fire
// period of the shooter's hull (R1135), and that event carries the damage of
// that one shot, the victim, the shooter's point and the two factions. This layer turns EVERY event into ONE SALVO: the rounds
// of the picked shot look fly from the source point to the victim, they follow
// the victim while it moves, and each one spawns the picked hit look when it
// arrives. An EXPLOSION of a fleet draws the picked final-blow look in place of
// today's boom.
//
// **What the layer decides and what it does not.** The World theme names six
// looks and `render/combat-styles.ts` resolves them; this layer owns the clock,
// the pools, the cull, the atlas, the hull record and the two resolutions the
// wire cannot state:
//
//   - THE FAMILY of the shot is the ROLE of the shooter hull (R1009). The event
//     carries no class, so the layer reads the visible fleet nearest the source
//     point; when the viewer cannot see the shooter it picks by the distance
//     source-to-victim against the reaches of the hello catalog, over the two
//     MOBILE families alone (R1014). It NEVER asks the server, because an
//     answer would leak the class of an undetected hull.
//   - THE KIND of the hit is `hitShield` while the victim's shields hold and
//     `hitHull` once they are down, read off the viewer's own state.
//   - WHAT IS LEFT of the victim's shields, 0 to 1, read fresh off the feed
//     every frame and handed to the mark as `HitCtx.shieldFrac` (R1011). A
//     card that states the shield reads that and never the mark's own age.
//   - WHICH MARK of a salvo may redraw the whole hull: the newest one of each
//     victim carries `HitCtx.lead` (R1011).
//   - WHICH HIT OPENS A FIGHT (R1979): a hit on a victim that took no hit
//     inside the calm gate of the shield regen (`CombatFeeds.fightGapSec`).
//     The opening hit keeps the full shield look, and every later shield
//     hit of that fight draws at `COMBAT_LIMITS.followHitAlpha`.
//
// **The flight stands on the FAMILY's own footing** (R1013). A shot row states
// `flightSec` at the separation its own mockup stage drew, `COMBAT_FAMILIES`
// holds that separation, the flight ceiling and the salvo span, and the layer
// scales the row by the distance of the fight.
//
// **The figure waits for the round** (R1010). `fire` answers how long the
// damage figure must wait for the first round of the salvo to land, capped at
// `figureWaitMaxSec`, and the caller hands that to `DamageLayer.show`. The
// number and the mark then arrive together.
//
// **A look may own the victim's hull, and there are two of those** (R1012).
// `ownedHulls` names every fleet a live FINAL BLOW draws: the fleet is gone,
// so the fleet layer takes its whole readout away (`setSuppressed`).
// `drawnHulls` names every fleet a live HULL or SHIELD mark draws: the fleet
// is alive, so the fleet layer takes the hull MARK alone and keeps the bars,
// the badge, the group mark, the wake and the selection
// (`setHullSuppressed`). Both are the map's answer to `HK.erase` of the mockup
// pages, and both are exact rather than an overpaint in the water ink.
//
// **The layer reads no reduced-motion switch** (`combat-recipe-kit.ts` states
// why): a round in flight is the event itself, and a frozen round would hang
// over the water and say nothing.
//
// **§10.** Every round and every mark comes out of a pool that is allocated
// once and never grows; the caps are `COMBAT_LIMITS`. A fight outside the
// padded viewport packs nothing. A hidden document packs nothing. A salvo that
// would not fit under the round cap drops its ROUNDS first, down to
// `minRounds`, because a salvo of one round still says who shot whom.

import { ShipClass } from '../../gen/proto/aquaphobia';
import { COMBAT_FAMILIES, COMBAT_LIMITS } from '../../gen/presentation';
import { factionColor, MPX_PER_PX } from '../../world';
import { shipStyleOf } from '../../render/hull-styles';
import {
  AUTO_SCENE_LOOK, type SceneLook,
} from '../../render/scene-look';
import {
  HIT_FIELDS, SHOT_FIELDS, dirOf, hash, hitLook, shotLook,
  type Dir, type HitCtx, type HitField, type HitLook, type HullMark, type Pt,
  type ShotCtx, type ShotField, type ShotLook,
} from '../../render/combat-styles';
import { hullKey } from '../gfx/atlas';
import { F_ADD, outside, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type {
  BatchSource, CircleSink, QuadSink, SegmentSink, SpriteEntry,
} from './batches';
import { holdScale, zoomOf } from './shared';
import { hiddenDocument } from '../../render/motion';
import { clamp, clamp01 } from '../../render/recipe-kit';
import { monsterKindOf } from '../../render/monster-kinds';

/** What `fire` is told about one event. Lengths are world px. */
export interface CombatEvent {
  /** `hit` is a gun exchange, `sink` the final blow on a fleet. */
  kind: 'hit' | 'sink';
  /** The struck fleet's id, 0 where the server named nobody. */
  victim: number;
  /** Where the event landed, as the fallback anchor for an unknown victim. */
  x: number;
  y: number;
  /** The shooter's point. 0/0 where nothing fired. */
  sourceX: number;
  sourceY: number;
  /** The shooter's faction, which the rounds wear. */
  faction: number;
  /** The struck fleet's faction, which a hull mark wears. */
  victimFaction: number;
  /** The shooter's account, which names the hull when the viewer owns it. */
  actor: number;
  /**
   * The victim's shields, 0 to 100, or −1 where the viewer cannot read them.
   *
   * Above 0 the hit takes `hitShield`, else `hitHull`. An unknown reading takes
   * `hitHull`, because a contact the client cannot read is a contact whose
   * shields it has never seen hold.
   */
  shieldPct: number;
  /**
   * The shooter's `ShipClass`, where the caller already knows it. 0 asks the
   * layer to resolve it (R1009).
   *
   * The live map always passes 0: an answer from the server would leak the
   * class of an undetected hull. The REPLAY FILM passes the real class, because
   * a replay is the record of a finished match and names every hull in it.
   */
  shooterCls?: number;
}

/** One hull the viewer can see, as the layer reads it from the fleet layer. */
export interface CombatHullPose {
  x: number;
  y: number;
  /** The heading in radians: +x is the bow. */
  head: number;
  faction: number;
  /** The `ShipClass` number. 0 is the dart of an unread contact. */
  cls: number;
}

/** One ship row of the hello catalog, as the family resolution reads it. */
export interface CombatShipRow {
  /** The `ShipClass` number. */
  cls: number;
  /** The pack word: skirmish, brawler, artillery, torpedo, tower or support. */
  role: string;
  /** The gun reach in world px, and the reach of a planted anchor. */
  rangePx: number;
  anchorRangePx: number;
}

/** Where the layer gets what it cannot hold itself. */
export interface CombatFeeds {
  /** Where a fleet is now, or null when the client has lost sight of it. */
  at(fleetId: number): { x: number; y: number } | null;
  /**
   * What a fleet's shields still hold, 0 to 100, or −1 where the viewer
   * cannot read them (R1011).
   *
   * A shield mark states what is LEFT of the shield, so it reads the victim's
   * own state on every frame. A contact that went dark answers −1, and the
   * mark then keeps the figure its own event carried.
   */
  shieldOf(fleetId: number): number;
  /** One fleet's pose, or null. A sink look needs the hull it draws. */
  pose(fleetId: number): CombatHullPose | null;
  /** The nearest visible fleet to a point inside `radius`, or null. */
  near(x: number, y: number, radius: number): { id: number; cls: number } | null;
  /** Which account owns a fleet, or 0. An own hull names its shooter exactly. */
  ownerOf(fleetId: number): number;
  /**
   * How long a hull may go without a hit and stay in one fight, in seconds of
   * the layer clock (R1979). It is the calm gate of the shield regen
   * (`Hello.regen_calm_map_sec`) at the map scale, so the hit that opens a
   * fight is the hit that starts the regen wait. 0 makes every hit open.
   */
  fightGapSec(): number;
}

/** Which shot field a role fires (R1009). */
const FAMILY_BY_ROLE: Readonly<Record<string, ShotField>> = {
  skirmish: 'shotMelee',
  brawler: 'shotMelee',
  torpedo: 'shotMelee',
  artillery: 'shotArtillery',
  tower: 'shotSentinel',
  // A support hull mounts no gun, so it raises no gun HIT event. The entry is
  // here so the table answers every role the pack spells.
  support: 'shotMelee',
};

/** The field a family with no catalog row falls back to. */
const DEFAULT_FAMILY: ShotField = 'shotMelee';

/**
 * The families a DISTANCE alone may name (R1014).
 *
 * A planted tower does not move: it stands at the site it was set on, and a
 * viewer who cannot see a hull at the source point has read nothing that says
 * a tower is there. The tower's band is the narrowest of the three as well,
 * and it lies inside the window an artillery hull fires in, so the distance
 * evidence for it is the weakest of the three. A shot the viewer cannot see
 * therefore reads as a close-in shot or as a siege, never as a tower.
 */
const DISTANCE_FAMILIES: readonly ShotField[] = ['shotMelee', 'shotArtillery'];

/**
 * The widest error the wire's mpx rounding puts on a distance between two
 * event points: half a mpx on each of four coordinates, rounded up (R1229).
 */
const WIRE_ROUND_PX = 2 / MPX_PER_PX;

/** The hull bloom cell the fleet layer draws under a hull (`atlas.ts`). */
const BLOOM_KEY = 'bloom';

/** The shortest wait between two prunes of the fight map, in seconds. */
const PRUNE_MIN_SEC = 5;

/** How many instances each sink holds before the engine grows it. */
const QUAD_CAPACITY = 1024;
const SEGMENT_CAPACITY = 1024;
const CIRCLE_CAPACITY = 512;

/** One round in flight. Allocated once, at construction (R131). */
interface Round {
  shot: ShotLook;
  hit: HitLook;
  victim: number;
  /** The muzzle, in world px. It does not move: the shooter may be unseen. */
  sx: number;
  sy: number;
  /** The round's target, as an OFFSET from the victim's anchor. */
  ox: number;
  oy: number;
  /** The victim's anchor, refreshed every frame while the victim is in sight. */
  ax: number;
  ay: number;
  /** The layer clock this round leaves the muzzle at. */
  start: number;
  flight: number;
  k: number;
  seed: number;
  ink: number;
  victimFaction: number;
  /** The victim's shields at the event, 0 to 1, or −1 for an unread contact. */
  shield: number;
  /** The alpha the mark draws at: 1, or the follow-up figure (R1979). */
  alpha: number;
}

/** One hit mark on the water. */
interface Mark {
  hit: HitLook;
  victim: number;
  /** The arrival point, as an OFFSET from the victim's anchor. */
  ox: number;
  oy: number;
  ax: number;
  ay: number;
  /** The heading of the hull when the mark was born, for a look that sinks it. */
  head: number;
  dirX: number;
  dirY: number;
  /** The layer clock this mark was born at. */
  born: number;
  life: number;
  /** The round's index in its own salvo, 0 for a sink (R1011). */
  k: number;
  seed: number;
  ink: number;
  /** The victim's shields at the event, 0 to 1, or −1 for an unread contact. */
  shield: number;
  ownsHull: boolean;
  /** True where the VICTIM is gone: the final blow of a sunk fleet (R1012). */
  gone: boolean;
  /** True while this mark is the newest one on its victim (R1011). */
  lead: boolean;
  /** The alpha the mark draws at: 1, or the follow-up figure (R1979). */
  alpha: number;
}

export class CombatLayer implements Layer {
  readonly name = 'combat';

  private readonly quads: QuadSink;
  private readonly segs: SegmentSink;
  private readonly rings: CircleSink;
  private readonly sprite: (key: string) => SpriteEntry;

  /** The pools. `rounds`/`marks` hold the live ones at the front. */
  private readonly rounds: Round[] = [];
  private readonly marks: Mark[] = [];
  private nRounds = 0;
  private nMarks = 0;

  /** The layer clock in seconds. A strobe and a spin read it. */
  private t = 0;
  private shown = true;
  private on = true;

  private look: SceneLook = AUTO_SCENE_LOOK;
  /** The look ids a preview knob forces, by field. '' is no override. */
  private readonly forced: Record<string, string> = {};

  /** The class-to-family table and the reach ladder, from the hello catalog. */
  private readonly familyByClass = new Map<number, ShotField>();
  private readonly reach: { field: ShotField; maxPx: number }[] = [];

  /** The hull keys a look decides, by `cls * 2 + hollow`. */
  private readonly hullKeys = new Map<number, string>();

  /** The fleets a live FINAL BLOW draws itself: the fleet is gone whole. */
  private readonly owned = new Set<number>();

  /**
   * The fleets a live HULL or SHIELD mark draws the silhouette of (R1012).
   *
   * The fleet is alive and still carries its bars, its badge and its group
   * mark; the layer takes its hull mark alone, because the recipe draws that
   * mark itself.
   */
  private readonly drawn = new Set<number>();

  /**
   * The layer clock of the last hit on each victim (R1979). A hit inside the
   * fight gap of the one before it is a follow-up. `update` drops a victim
   * once the gap has passed, so the map holds the fights of the last gap.
   */
  private readonly lastHit = new Map<number, number>();
  private nextPrune = 0;

  /** The scratch of the lead walk: one victim and one mark slot each (R131). */
  private readonly leadVictim = new Int32Array(COMBAT_LIMITS.maxHits);
  private readonly leadSlot = new Int32Array(COMBAT_LIMITS.maxHits);

  /** The one reused context of each kind, and the one reused scratch (R131). */
  private readonly shotCtx: ShotCtx;
  private readonly hitCtx: HitCtx;
  private readonly dir: Dir = { ux: 1, uy: 0, nx: 0, ny: 1, len: 1, ang: 0 };
  private readonly from: Pt = { x: 0, y: 0 };
  private readonly to: Pt = { x: 0, y: 0 };
  private readonly at: Pt = { x: 0, y: 0 };
  private readonly hull: HullMark;

  /**
   * Past this camera zoom every mark holds the size it has AT this zoom, in
   * place of growing with the map (R552). 0, the live map's answer, never
   * holds. The replay film sets it, because a film zooms in on a fight.
   */
  holdZoom = 0;

  /** The map's own answers, kept so a borrower can be given the layer back. */
  private readonly homeFeeds: CombatFeeds;
  private feeds: CombatFeeds;
  private lent = false;

  constructor(src: BatchSource, feeds: CombatFeeds) {
    this.homeFeeds = feeds;
    this.feeds = feeds;
    this.quads = src.newQuads(QUAD_CAPACITY);
    this.segs = src.newSegments(SEGMENT_CAPACITY);
    this.rings = src.newCircles(CIRCLE_CAPACITY);
    this.sprite = (key) => src.sprite(key);
    for (let i = 0; i < COMBAT_LIMITS.maxRounds; i++) this.rounds.push(blankRound());
    for (let i = 0; i < COMBAT_LIMITS.maxHits; i++) this.marks.push(blankMark());
    this.hull = {
      x: 0, y: 0, head: 0, ink: 0xffffff, halfLenPx: 0, halfBeamPx: 0,
      sprite: (hollow) => this.sprite(this.hullKeyOf(this.hullCls, hollow)),
      bloom: () => this.sprite(BLOOM_KEY),
    };
    const base = {
      t: 0, z: 1, ink: 0xffffff, alpha: 1, add: F_ADD,
      quads: this.quads, segs: this.segs, rings: this.rings,
      sprite: this.sprite,
    };
    this.shotCtx = {
      ...base, row: shotLook('shotMelee', '').row,
      from: this.from, to: this.to, p: 0, k: 0, seed: 0, d: this.dir,
    };
    this.hitCtx = {
      ...base, row: hitLook('hitShield', '').row,
      at: this.at, dirX: 1, dirY: 0, p: 0, k: 0, seed: 0, hull: null,
      shieldFrac: -1, lead: true,
    };
    this.rebuildKeys();
  }

  /** The class the hull record answers for, while one mark is packed. */
  private hullCls = 0;

  get visible(): boolean { return this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  /** How many rounds and how many marks are alive. The stats line reads it. */
  get roundCount(): number { return this.nRounds; }
  get markCount(): number { return this.nMarks; }

  /** The fleets a live final blow draws (`FleetLayer.setSuppressed`). */
  get ownedHulls(): ReadonlySet<number> { return this.owned; }

  /** The fleets a live hit mark draws (`FleetLayer.setHullSuppressed`). */
  get drawnHulls(): ReadonlySet<number> { return this.drawn; }

  /**
   * The `#shots=0` knob: 0 takes the layer off and the caller's own ring back
   * (`MapScene`). It is a measurement and eye-test knob and NOT a viewer
   * setting, the way `#effects` is (R914).
   */
  setEnabled(on: boolean): void {
    this.on = on;
    if (!on) this.clear();
  }

  /** True while the layer draws. The caller asks before it fires a ring. */
  get enabled(): boolean { return this.on; }

  /**
   * Lends the layer to another feed, which is the replay film (R1007).
   *
   * A film plays hulls of its own on the live engine, so a salvo it fires must
   * follow the REPLAY pose of the hulls and not the live fleets of the same id.
   * The film installs its own answers on open and hands the layer back with
   * `null` on close. While the layer is borrowed the live map fires nothing
   * into it, the rule `DamageLayer.setAnchorSource` states.
   */
  setFeeds(feeds: CombatFeeds | null): void {
    this.feeds = feeds ?? this.homeFeeds;
    this.lent = feeds !== null;
    this.clear();
  }

  /** True while another feed owns the rounds. The live feed reads it. */
  get borrowed(): boolean { return this.lent; }

  /** The canvas half of the look (R814): the six gun fields and the hull set. */
  setLook(look: SceneLook): void {
    this.look = look;
    this.rebuildKeys();
  }

  /**
   * The preview knobs `#shotmelee=<id>` and its five siblings (R925's pattern):
   * one look id every fight of that field wears instead of the theme's. '' is
   * no override. The knob changes nothing on the wire and it is no setting.
   */
  setForced(forced: Readonly<Record<string, string>>): void {
    for (const f of SHOT_FIELDS) this.forced[f] = forced[f] ?? '';
    for (const f of HIT_FIELDS) this.forced[f] = forced[f] ?? '';
  }

  /**
   * Which FIELD every event takes, for the theme preview (R806, R1007).
   *
   * The preview shows one row at a time, and a row names one field. The staged
   * fight is one pair of hulls, so the preview cannot make an artillery hull
   * fire or strip a shield to show a hull hit; it says which field to read
   * instead. '' puts the map's own resolution back, and the live map and the
   * film never call this.
   */
  setPreviewFields(shot: ShotField | '', hit: HitField | ''): void {
    this.forcedShotField = shot;
    this.forcedHitField = hit;
  }

  private forcedShotField: ShotField | '' = '';
  private forcedHitField: HitField | '' = '';

  /**
   * The ship roster of the hello catalog, which the family resolution reads
   * (R1009). It states no figure of its own: the roles and the reaches are the
   * server's own table.
   */
  setRoster(rows: readonly CombatShipRow[]): void {
    this.familyByClass.clear();
    const maxOf = new Map<ShotField, number>();
    for (const r of rows) {
      const field = FAMILY_BY_ROLE[r.role] ?? DEFAULT_FAMILY;
      this.familyByClass.set(r.cls, field);
      // The tower stands OUTSIDE the distance ladder (R1014): a distance alone
      // must never name the tower family. Its reach is left out whole, so it
      // widens no other band either.
      if (!DISTANCE_FAMILIES.includes(field)) continue;
      const reach = Math.max(r.rangePx, r.anchorRangePx);
      if (reach <= 0) continue;
      const had = maxOf.get(field) ?? 0;
      if (reach > had) maxOf.set(field, reach);
    }
    this.reach.length = 0;
    for (const [field, maxPx] of maxOf) this.reach.push({ field, maxPx });
    this.reach.sort((a, b) => a.maxPx - b.maxPx);
  }

  /** Takes every round and every mark off the water (a season roll, a seek). */
  clear(): void {
    this.nRounds = 0;
    this.nMarks = 0;
    this.owned.clear();
    this.drawn.clear();
    this.lastHit.clear();
  }

  // -- what the feed fires ---------------------------------------------------

  /**
   * One event. The answer is how long the damage figure must wait for the
   * first round to land, in seconds (R1010): 0 when nothing flies.
   */
  fire(ev: CombatEvent): number {
    if (!this.on) return 0;
    const anchor = this.anchorOf(ev.victim);
    const ax = anchor ? anchor.x : ev.x;
    const ay = anchor ? anchor.y : ev.y;
    const shieldFrac = ev.shieldPct < 0 ? -1 : clamp01(ev.shieldPct / 100);
    if (ev.kind === 'sink') {
      const look = this.hitOf(this.forcedHitField || 'hitSink');
      this.spawnMark(
        look, ev.victim, 0, 0, 0, ax, ay, this.headOf(ev.victim),
        ev.sourceX, ev.sourceY, ev.victimFaction || ev.faction, shieldFrac, true, 1,
      );
      this.lastHit.delete(ev.victim);
      return 0;
    }
    // Every hit restarts the calm gate of the victim, so the hit is noted
    // before a cap can drop its salvo (R1979).
    const opening = this.opens(ev.victim);
    // Every HIT event draws its salvo (R1135, R1136). One event is one shot
    // of the sim, so the layer holds no gate. A span longer than the wall
    // fire period overlaps the salvos of one pair; the fixed pools bound
    // that, and the §10 caps drop rounds before marks.
    const field = this.forcedShotField || this.familyOf(ev);
    const shot = this.shotOf(field);
    const held = ev.shieldPct > 0;
    const hit = this.hitOf(this.forcedHitField || (held ? 'hitShield' : 'hitHull'));
    // A follow-up shield hit draws dimmed. A hull hit keeps its full look,
    // because a hull with no shield left is the news of the fight.
    const alpha = held && !opening ? COMBAT_LIMITS.followHitAlpha : 1;
    // A monster fires no gun (R2719, round 109): its body strikes in the
    // monster layer, so its hit flies no salvo and the mark lands at once.
    if (this.monsterShot(ev)) {
      this.spawnMark(hit, ev.victim, 0, 0, 0, ax, ay, this.headOf(ev.victim),
        ev.sourceX, ev.sourceY, ev.victimFaction || ev.faction, shieldFrac, false, alpha);
      return 0;
    }
    const row = shot.row;
    // The flight scales with the distance, on the FAMILY's own footing: the
    // row states its figure at the separation its mockup stage drew, and the
    // family's own ceiling holds it (R1013). A lobbed shell may therefore
    // stay in the water longer than a point-blank round.
    const fam = COMBAT_FAMILIES[field];
    const dist = Math.hypot(ax - ev.sourceX, ay - ev.sourceY);
    const flight = clamp(
      row.flightSec * (dist / fam.refPx),
      COMBAT_LIMITS.flightMinSec, fam.flightMaxSec,
    );
    // The rounds, dropped to what the cap and the span allow. The cap drops
    // rounds first, because a salvo of one round still says who shot whom.
    const free = COMBAT_LIMITS.maxRounds - this.nRounds;
    let rounds = Math.min(row.rounds, Math.max(COMBAT_LIMITS.minRounds, free));
    if (rounds > free) return 0;
    let step = row.stepSec;
    const span = fam.spanMaxSec - flight;
    if (rounds > 1 && step * (rounds - 1) > span) step = span / (rounds - 1);
    if (step < 0) { step = 0; rounds = 1; }
    const ink = factionColor(ev.faction);
    const seedBase = (ev.victim * 131 + Math.round(this.t * 1000)) | 0;
    for (let k = 0; k < rounds; k++) {
      const seed = (seedBase + k * 7919) | 0;
      // The round's own target: the anchor pushed by the jitter its row
      // states, as a polar offset off a pure hash — the rule of the mockup
      // harness, so the round and its mark agree on the point.
      const a = hash(seed, 11) * Math.PI * 2;
      const rr = Math.sqrt(hash(seed, 12)) * row.jitterPx;
      const r = this.rounds[this.nRounds++];
      r.shot = shot; r.hit = hit;
      r.victim = ev.victim;
      r.sx = ev.sourceX; r.sy = ev.sourceY;
      r.ox = Math.cos(a) * rr; r.oy = Math.sin(a) * rr;
      r.ax = ax; r.ay = ay;
      r.start = this.t + k * step;
      r.flight = flight;
      r.k = k;
      r.seed = seed;
      r.ink = ink;
      r.victimFaction = ev.victimFaction || ev.faction;
      r.shield = shieldFrac;
      r.alpha = alpha;
    }
    return Math.min(flight, COMBAT_LIMITS.figureWaitMaxSec);
  }

  /**
   * Notes a hit on `victim` and answers whether it opens a fight (R1979): the
   * victim took no hit inside the fight gap. A hit with no named victim and a
   * gap of 0 always open, which is the full look of every hit.
   */
  private opens(victim: number): boolean {
    if (victim === 0) return true;
    const gap = this.feeds.fightGapSec();
    const last = this.lastHit.get(victim);
    this.lastHit.set(victim, this.t);
    return !(gap > 0 && last !== undefined && this.t - last < gap);
  }

  // -- the frame -------------------------------------------------------------

  update(frame: FrameInfo): void {
    this.t += frame.dtMs / 1000;
    if (this.t >= this.nextPrune) this.pruneFights();
    this.quads.begin();
    this.segs.begin();
    this.rings.begin();
    // `setEnabled(false)` cleared both hull sets, and nothing fills them while
    // the layer is off.
    if (!this.on) return;
    const zoom = zoomOf(frame.cam.zoom);
    // The hold of R552: past `holdZoom` a figure keeps the size it had there.
    const k = holdScale(this.holdZoom, zoom);
    const pad = COMBAT_LIMITS.cullPadPx;
    const x0 = frame.cull.x0 - pad, y0 = frame.cull.y0 - pad;
    const x1 = frame.cull.x1 + pad, y1 = frame.cull.y1 + pad;
    const drawing = this.shown && !hiddenDocument();

    // The marks first, then the rounds. A round that arrives this frame is a
    // mark of age zero, and a mark this frame made must not be aged by the
    // step that made it.
    this.owned.clear();
    this.drawn.clear();
    for (let i = this.nMarks - 1; i >= 0; i--) {
      const m = this.marks[i];
      if (this.t - m.born > m.life) {
        this.marks[i] = this.marks[--this.nMarks];
        this.marks[this.nMarks] = m;
      }
    }
    // Which mark of each victim may redraw the whole hull (R1011). A salvo is
    // several marks on one hull, and a look that redraws the silhouette would
    // stack them, so the NEWEST mark of a victim leads and the rest draw their
    // own strike alone. The scratch is a module array, so nothing is allocated.
    this.markLeads();
    for (let i = this.nMarks - 1; i >= 0; i--) {
      const m = this.marks[i];
      const age = this.t - m.born;
      // A look that owns the hull keeps it off the fleet layer even where the
      // mark is culled: a hull that reappeared off screen and vanished again
      // on screen would read as a second ship. A SUNK fleet is gone whole; a
      // hull hit takes the silhouette alone and keeps its bars (R1012).
      if (m.ownsHull && m.lead && m.victim !== 0) {
        if (m.gone) this.owned.add(m.victim); else this.drawn.add(m.victim);
      }
      // A mark follows its hull while the hull is in sight. A contact that
      // went dark keeps the last place it was seen.
      const anchor = this.anchorOf(m.victim);
      if (anchor) { m.ax = anchor.x; m.ay = anchor.y; }
      // The shield figure is read FRESH every frame, so a mark that states
      // what is left of the shield states what is left now (R1011). A contact
      // that went dark keeps the figure its own event carried.
      if (m.victim !== 0) {
        const pct = this.feeds.shieldOf(m.victim);
        if (pct >= 0) m.shield = clamp01(pct / 100);
      }
      if (!drawing) continue;
      const ax = m.ax + m.ox, ay = m.ay + m.oy;
      if (outside(ax, ay, x0, y0, x1, y1)) continue;
      this.packMark(m, age, ax, ay, zoom, k);
    }

    // The rounds. One arrives and becomes a mark, whether or not it drew.
    for (let i = this.nRounds - 1; i >= 0; i--) {
      const r = this.rounds[i];
      const age = this.t - r.start;
      const anchor = this.anchorOf(r.victim);
      if (anchor) { r.ax = anchor.x; r.ay = anchor.y; }
      if (age >= r.flight) {
        this.land(r);
        this.rounds[i] = this.rounds[--this.nRounds];
        this.rounds[this.nRounds] = r;
        continue;
      }
      if (age < 0 || !drawing) continue;
      const tx = r.ax + r.ox, ty = r.ay + r.oy;
      if (outside(tx, ty, x0, y0, x1, y1) && outside(r.sx, r.sy, x0, y0, x1, y1)) continue;
      this.packRound(r, age, tx, ty, zoom, k);
    }
  }

  draw(ctx: DrawCtx): void {
    // The rings and the lines first, the sprites over them: a flare that a
    // beam crossed must read as the top of the mark.
    this.rings.flush(ctx.cam);
    this.segs.flush(ctx.cam);
    this.quads.flush(ctx.cam);
  }

  /** Drops every victim whose fight gap has passed (R1979). */
  private pruneFights(): void {
    const gap = this.feeds.fightGapSec();
    for (const [victim, at] of this.lastHit) {
      if (!(gap > 0) || this.t - at >= gap) this.lastHit.delete(victim);
    }
    this.nextPrune = this.t + Math.max(gap, PRUNE_MIN_SEC);
  }

  /**
   * Names the newest live mark of every victim, and clears the rest (R1011).
   *
   * A look that redraws the victim's whole silhouette must run ONCE a frame
   * per hull: a salvo is several marks on one hull, and two hulls drawn over
   * each other read as two ships at one place. The newest mark leads, because
   * the newer strike is the one a reader is looking at.
   *
   * The walk is one pass over the live marks and a short scan of the victims
   * it has already seen. Both scratches are typed arrays the layer owns, so it
   * allocates nothing (R131).
   */
  private markLeads(): void {
    let seen = 0;
    for (let i = 0; i < this.nMarks; i++) {
      const m = this.marks[i];
      m.lead = false;
      if (m.victim === 0) { m.lead = true; continue; }
      let slot = -1;
      for (let j = 0; j < seen; j++) {
        if (this.leadVictim[j] === m.victim) { slot = j; break; }
      }
      if (slot < 0) {
        this.leadVictim[seen] = m.victim;
        this.leadSlot[seen] = i;
        seen++;
        continue;
      }
      if (m.born >= this.marks[this.leadSlot[slot]].born) this.leadSlot[slot] = i;
    }
    for (let j = 0; j < seen; j++) this.marks[this.leadSlot[j]].lead = true;
  }

  /** Where a victim stands now, or null for no victim or one out of sight. */
  private anchorOf(victim: number): { x: number; y: number } | null {
    return victim === 0 ? null : this.feeds.at(victim);
  }

  /** A victim's pose, or null for no victim or one out of sight. */
  private poseOf(victim: number): CombatHullPose | null {
    return victim === 0 ? null : this.feeds.pose(victim);
  }

  /** A victim's heading, or 0 for no victim or one out of sight. */
  private headOf(victim: number): number {
    return this.poseOf(victim)?.head ?? 0;
  }

  // -- the resolutions -------------------------------------------------------

  /**
   * Which shot family a fight takes (R1009).
   *
   * The visible hull nearest the source point names the shooter, and the hull
   * the event's own `actor` owns is preferred where two stand that near. With
   * no hull in reach the distance decides, over the MOBILE families alone
   * (R1014): the narrowest of them whose widest reach still covers the
   * distance, and the widest past every reach. The layer asks the server
   * nothing, so the class of an undetected shooter cannot leak.
   *
   * The distance is the one the SERVER fired at (R1229): the source point to
   * the event's own victim point, both from the shot's tick. The drawn anchor
   * of the victim lags or leads the sim by some px, and a Cutlass that fires
   * at its full 50 px then read as a siege gun. The margin absorbs the mpx
   * rounding of the two wire points, so a shot at the exact reach stays in
   * its band.
   */
  private familyOf(ev: CombatEvent): ShotField {
    if (ev.shooterCls !== undefined && ev.shooterCls > 0) {
      const named = this.familyByClass.get(ev.shooterCls);
      if (named) return named;
    }
    if (ev.sourceX !== 0 || ev.sourceY !== 0) {
      const near = this.feeds.near(ev.sourceX, ev.sourceY, COMBAT_LIMITS.shooterSnapPx);
      // A hull whose account is not the actor still names the family: the
      // server said the shot came from that point, and the nearest hull there
      // is the best reading the viewer has. `ownerOf` only breaks a tie.
      if (near && near.cls !== ShipClass.SHIP_CLASS_UNSPECIFIED) {
        const field = this.familyByClass.get(near.cls);
        if (field) return field;
      }
    }
    const dist = Math.hypot(ev.x - ev.sourceX, ev.y - ev.sourceY);
    for (const r of this.reach) if (dist <= r.maxPx + WIRE_ROUND_PX) return r.field;
    const last = this.reach[this.reach.length - 1];
    return last ? last.field : DEFAULT_FAMILY;
  }

  /**
   * Whether a hit comes from a monster (R2719): the class the film names,
   * else the class of the hull nearest the source point, the reading
   * `familyOf` takes. A monster's class is always public.
   */
  private monsterShot(ev: CombatEvent): boolean {
    if (ev.shooterCls !== undefined && ev.shooterCls > 0) return monsterKindOf(ev.shooterCls) >= 0;
    if (ev.faction !== 0 || (ev.sourceX === 0 && ev.sourceY === 0)) return false;
    const near = this.feeds.near(ev.sourceX, ev.sourceY, COMBAT_LIMITS.shooterSnapPx);
    return near !== null && monsterKindOf(near.cls) >= 0;
  }

  /** The look id one field wears: the preview knob's, else the theme's. */
  private lookId(field: ShotField | HitField): string {
    return this.forced[field] || this.look[field];
  }

  private shotOf(field: ShotField): ShotLook { return shotLook(field, this.lookId(field)); }
  private hitOf(field: HitField): HitLook { return hitLook(field, this.lookId(field)); }

  /** The atlas key of one hull, under the look's own ship icon set (R835). */
  private hullKeyOf(cls: number, hollow: boolean): string {
    return this.hullKeys.get(cls * 2 + (hollow ? 1 : 0))
      ?? hullKey(shipStyleOf(this.look, ShipClass.SHIP_CLASS_UNSPECIFIED),
        ShipClass.SHIP_CLASS_UNSPECIFIED, hollow);
  }

  /** The keys a look decides, built once per look — the `fleets.ts` rule. */
  private rebuildKeys(): void {
    this.hullKeys.clear();
    for (const cls of Object.values(ShipClass)) {
      if (typeof cls !== 'number') continue;
      const style = shipStyleOf(this.look, cls);
      this.hullKeys.set(cls * 2, hullKey(style, cls, false));
      this.hullKeys.set(cls * 2 + 1, hullKey(style, cls, true));
    }
  }

  // -- the pools -------------------------------------------------------------

  /** A round arrived: it becomes the mark its own event picked. */
  private land(r: Round): void {
    this.spawnMark(
      r.hit, r.victim, r.k, r.ox, r.oy, r.ax, r.ay, this.headOf(r.victim),
      r.sx, r.sy, r.victimFaction, r.shield, false, r.alpha,
    );
  }

  /**
   * One mark at the anchor `(ax, ay)` pushed by `(ox, oy)`, struck from the
   * muzzle `(sx, sy)`. The strike direction runs muzzle to arrival point;
   * with no source it points straight up, the damage figure's own rule.
   */
  private spawnMark(
    hit: HitLook, victim: number, k: number, ox: number, oy: number,
    ax: number, ay: number, head: number, sx: number, sy: number,
    faction: number, shield: number, gone: boolean, alpha: number,
  ): void {
    if (this.nMarks >= COMBAT_LIMITS.maxHits) {
      // The oldest mark is the one a reader has already read, so the cap drops
      // the front and never refuses the mark that just landed.
      const oldest = this.marks[0];
      this.marks[0] = this.marks[this.nMarks - 1];
      this.marks[this.nMarks - 1] = oldest;
      this.nMarks--;
    }
    const m = this.marks[this.nMarks++];
    m.hit = hit;
    m.victim = victim;
    m.ox = ox; m.oy = oy;
    m.ax = ax; m.ay = ay;
    m.head = head;
    const dx = ax + ox - sx, dy = ay + oy - sy;
    const n = Math.hypot(dx, dy);
    m.dirX = n > 1e-6 ? dx / n : 0;
    m.dirY = n > 1e-6 ? dy / n : -1;
    m.born = this.t;
    m.life = hit.row.lifeSec;
    m.k = k;
    m.seed = (victim * 7919 + Math.round(this.t * 1000)) | 0;
    m.ink = factionColor(faction);
    m.shield = shield;
    // A mark with no victim (a shot at a sea object, R3031) has no hull to
    // own, so a look that redraws the hull draws its strike alone.
    m.ownsHull = hit.ownsHull && victim !== 0;
    m.gone = gone;
    m.lead = true;
    m.alpha = alpha;
  }

  // -- the packs -------------------------------------------------------------

  private packRound(
    r: Round, age: number, tx: number, ty: number, zoom: number, k: number,
  ): void {
    const c = this.shotCtx;
    this.from.x = r.sx; this.from.y = r.sy;
    this.to.x = tx; this.to.y = ty;
    dirOf(this.from, this.to, this.dir);
    c.row = k === 1 ? r.shot.row : scaleShot(r.shot.row, k);
    c.t = this.t;
    c.z = zoom;
    c.ink = r.ink;
    c.alpha = 1;
    c.p = age / r.flight;
    c.k = r.k;
    c.seed = r.seed;
    r.shot.recipe(c);
  }

  private packMark(
    m: Mark, age: number, ax: number, ay: number, zoom: number, k: number,
  ): void {
    const c = this.hitCtx;
    this.at.x = ax; this.at.y = ay;
    c.row = k === 1 ? m.hit.row : scaleHit(m.hit.row, k);
    c.t = this.t;
    c.z = zoom;
    c.ink = m.ink;
    c.alpha = m.alpha;
    c.p = age / m.life;
    c.dirX = m.dirX; c.dirY = m.dirY;
    c.k = m.k;
    c.seed = m.seed;
    c.shieldFrac = m.shield;
    c.lead = m.lead;
    // The victim's hull, for a mark that lands on the plate or sinks it. A mark
    // that owns the hull keeps drawing the pose it was born with, because the
    // fleet is gone from the state and the feed answers nothing.
    const pose = this.poseOf(m.victim);
    if (pose || m.ownsHull) {
      this.hullCls = pose ? pose.cls : ShipClass.SHIP_CLASS_UNSPECIFIED;
      this.hull.x = pose ? pose.x : m.ax;
      this.hull.y = pose ? pose.y : m.ay;
      this.hull.head = pose ? pose.head : m.head;
      this.hull.ink = m.ink;
      const e = this.sprite(this.hullKeyOf(this.hullCls, false));
      this.hull.halfLenPx = e.w / 2;
      this.hull.halfBeamPx = e.h / 2;
      c.hull = this.hull;
    } else {
      c.hull = null;
    }
    m.hit.recipe(c);
  }
}

// ---------------------------------------------------------------------------
// The scratches
// ---------------------------------------------------------------------------

/**
 * The two scratch rows the R552 hold writes into.
 *
 * A film zooms in on a fight, and a mark the size of the hull it struck hides
 * the hull. `holdZoom` scales every WORLD LENGTH of the row, which is every
 * `_px` field and no other (R1052): `drift_px` is how far a mark moves, so it
 * scales with the rest. `speed` is an angle or a rate — radians, turns a
 * second, a flicker clock, a frame counter — and the two counts and the two
 * alphas are not lengths, so all five stay as they are. One object per kind
 * is rewritten in place, so the hold allocates nothing (R131).
 *
 * The two functions are exported for `combat.test.ts`, which pins the rule
 * field by field.
 */
const SHOT_SCRATCH = {
  flightSec: 0, rounds: 0, stepSec: 0, jitterPx: 0, rPx: 0, r2Px: 0,
  widthPx: 0, width2Px: 0, lengthPx: 0, length2Px: 0, driftPx: 0,
  alpha: 0, alpha2: 0, speed: 0, count: 0, count2: 0,
};
const HIT_SCRATCH = {
  lifeSec: 0, rPx: 0, r2Px: 0, widthPx: 0, width2Px: 0, lengthPx: 0,
  length2Px: 0, driftPx: 0, alpha: 0, alpha2: 0, speed: 0, count: 0, count2: 0,
};

export function scaleShot(row: typeof SHOT_SCRATCH, k: number): typeof SHOT_SCRATCH {
  const s = SHOT_SCRATCH;
  s.flightSec = row.flightSec; s.rounds = row.rounds; s.stepSec = row.stepSec;
  s.jitterPx = row.jitterPx * k;
  s.rPx = row.rPx * k; s.r2Px = row.r2Px * k;
  s.widthPx = row.widthPx * k; s.width2Px = row.width2Px * k;
  s.lengthPx = row.lengthPx * k; s.length2Px = row.length2Px * k;
  s.driftPx = row.driftPx * k;
  s.alpha = row.alpha; s.alpha2 = row.alpha2;
  s.speed = row.speed; s.count = row.count; s.count2 = row.count2;
  return s;
}

export function scaleHit(row: typeof HIT_SCRATCH, k: number): typeof HIT_SCRATCH {
  const s = HIT_SCRATCH;
  s.lifeSec = row.lifeSec;
  s.rPx = row.rPx * k; s.r2Px = row.r2Px * k;
  s.widthPx = row.widthPx * k; s.width2Px = row.width2Px * k;
  s.lengthPx = row.lengthPx * k; s.length2Px = row.length2Px * k;
  s.driftPx = row.driftPx * k;
  s.alpha = row.alpha; s.alpha2 = row.alpha2;
  s.speed = row.speed; s.count = row.count; s.count2 = row.count2;
  return s;
}

function blankRound(): Round {
  return {
    shot: shotLook('shotMelee', ''), hit: hitLook('hitHull', ''),
    victim: 0, sx: 0, sy: 0, ox: 0, oy: 0, ax: 0, ay: 0,
    start: 0, flight: 1, k: 0, seed: 0, ink: 0xffffff, victimFaction: 0,
    shield: -1, alpha: 1,
  };
}

function blankMark(): Mark {
  return {
    hit: hitLook('hitHull', ''), victim: 0, ox: 0, oy: 0, ax: 0, ay: 0,
    head: 0, dirX: 1, dirY: 0, born: 0, life: 1, k: 0, seed: 0, ink: 0xffffff,
    shield: -1, ownsHull: false, gone: false, lead: true, alpha: 1,
  };
}
